import type { EmbedEvent } from "../lib/api";
import type { WidgetConfig } from "../lib/config";
import { formatEventDate } from "../lib/date";
import { MarkdownText } from "./markdown-text";
import { TagPill } from "./tag-pill";

type EventCardProps = {
  event: EmbedEvent;
  config: WidgetConfig;
  onSelect: (eventId: string) => void;
};

export function EventCard({ event, config, onSelect }: EventCardProps) {
  return (
    <article className="gitg-item">
      <button
        className="gitg-item-button"
        onClick={() => onSelect(event.id)}
        type="button"
      >
        {config.showImage ? (
          event.image ? (
            <img
              className="gitg-item-image"
              src={event.image}
              alt=""
              aria-hidden="true"
            />
          ) : (
            <div className="gitg-item-image-placeholder" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
            </div>
          )
        ) : null}
        <div className="gitg-item-content">
          {event.event_type === "external" ? (
            <p className="gitg-external-notice">
              External event · organised by a third party
            </p>
          ) : event.event_type === "school" ? (
            <p className="gitg-external-notice">Closed school event</p>
          ) : null}
          <h3 className="gitg-item-title">{event.name}</h3>
          {config.showDetails || config.showLocation ? (
            <div className="gitg-meta">
              {config.showDetails ? <div>{formatEventDate(event)}</div> : null}
              {config.showLocation ? <div>{event.address_title}</div> : null}
              {config.showLocation ? <div>{event.address_full}</div> : null}
            </div>
          ) : null}
          {config.showDescription && event.description ? (
            <p className="gitg-description">
              <MarkdownText text={event.description} />
            </p>
          ) : null}
          {event.event_type === "external" ||
          (config.showSport && event.tags.length > 0) ||
          (config.showRecurrence && event.recurrence_frequency) ? (
            <div className="gitg-item-tags">
              {event.event_type === "external" ? (
                <TagPill
                  label="External"
                  className="gitg-label-external"
                  title="Third-party event — see the description for details"
                />
              ) : null}
              {config.showSport
                ? event.tags.map((tag) => (
                    <TagPill key={tag.id} label={tag.name} />
                  ))
                : null}
              {config.showRecurrence && event.recurrence_frequency ? (
                <TagPill
                  label={
                    event.recurrence_frequency.charAt(0).toUpperCase() +
                    event.recurrence_frequency.slice(1)
                  }
                  className="gitg-label-recurring"
                  title="Part of a recurring series"
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </button>
    </article>
  );
}
